import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
    Container,
    Typography,
    Box,
    CircularProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Link,
    IconButton, Collapse, Chip
} from "@mui/material";
import { motion } from "framer-motion";
import { getServerPlayers } from "../http/ServerAPI";
import { observer } from "mobx-react-lite";
import BreadCrumbs from "../components/Breadcrumbs";
import { SERVERS_ROUTE } from "../utils/consts";
import dayjs from "dayjs";
import CircleIcon from "@mui/icons-material/Circle";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import RedeemIcon from "@mui/icons-material/Redeem";
import SecurityIcon from "@mui/icons-material/Security";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import StarsIcon from "@mui/icons-material/Stars";
import LogoutIcon from "@mui/icons-material/Logout";
import BlockIcon from "@mui/icons-material/Block";
import Tooltip from "@mui/material/Tooltip";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button
} from "@mui/material";
import {sendRcon} from "../http/ServerAPI";
import {getApiErrorMessage} from "../http/apiError";

const sortFields = [
    { value: "game_times", label: "Игровое время" },
    { value: "name", label: "Имя" },
    { value: "steam_id", label: "Steam ID" },
    { value: "created_at", label: "Первая игра" },
    { value: "updated_at", label: "Был в игре" },
];

const PlayersPage = observer(() => {
    const { serverId } = useParams();
    const [players, setPlayers] = useState([]);
    const [filtered, setFiltered] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [sortBy, setSortBy] = useState("updated_at");

    const [selectedPlayer, setSelectedPlayer] = useState(null);

    const [vipModalOpen, setVipModalOpen] = useState(false);
    const [balanceModalOpen, setBalanceModalOpen] = useState(false);
    const [kickModalOpen, setKickModalOpen] = useState(false);
    const [banModalOpen, setBanModalOpen] = useState(false);

    const LIMIT = 50;
    const [showAll, setShowAll] = useState(false);

    const hrefs = [{ href: SERVERS_ROUTE, name: "Список серверов" }];

    useEffect(() => {
        const fetchPlayers = async () => {
            try {
                const res = await getServerPlayers(serverId);
                setPlayers(res || []);
            } catch (e) {
                console.error(getApiErrorMessage(e, 'Ошибка загрузки игроков'));
            } finally {
                setLoading(false);
            }
        };
        fetchPlayers();
    }, [serverId]);

    useEffect(() => {
        const lower = search.toLowerCase();
        let result = players.filter(p =>
            p.name?.toLowerCase().includes(lower) ||
            p.steam_id?.toLowerCase().includes(lower)
        );

        result = [...result].sort((a, b) => {
            if (sortBy === "game_times") return b.game_times - a.game_times;
            return String(b[sortBy] || "").localeCompare(String(a[sortBy] || ""));
        });

        setFiltered(result);
        setShowAll(false);
    }, [search, sortBy, players]);

    const isActive = (updatedAt) => {
        if (!updatedAt) return false;
        return dayjs().diff(dayjs(updatedAt), "minute") <= 15;
    };

    const isOldPlayer = (updatedAt) => {
        if (!updatedAt) return false;
        return dayjs().diff(dayjs(updatedAt), "month") >= 1;
    };

    const sendMessage = async (server_id, message) => {
        try {
            let data = await sendRcon(server_id, message, 'command');
            console.log(data);
        } catch (e) {
            console.error(getApiErrorMessage(e));
        }
    }

    const getTextStyle = (player) => {
        let color = "inherit";
        if (player.auth_name && player.auth_id) {
            color = "#97eca8";
        } else if (isOldPlayer(player.updated_at)) {
            color = "gray";
        } else if (player.game_times < 1000) {
            color = "#ecd897"; // темно-жёлтый
        }
        return { color };
    };

    const openModal = (type, player) => {
        setSelectedPlayer(player);

        if (type === "vip") setVipModalOpen(true);
        if (type === "balance") setBalanceModalOpen(true);
        if (type === "kick") setKickModalOpen(true);
        if (type === "ban") setBanModalOpen(true);
    };

    const hasActivePerms = (player) =>
        Array.isArray(player.timed_perms) && player.timed_perms.length > 0;

    const PlayerRow = ({ player }) => {
        const [open, setOpen] = useState(false);
        const active = dayjs().diff(dayjs(player.updated_at), "minute") <= 15;
        const vip = hasActivePerms(player);

        return (
            <>
                <TableRow hover>
                    {/* expand */}
                    <TableCell width={40}>
                        <IconButton size="small" onClick={() => setOpen(!open)}>
                            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                        </IconButton>
                    </TableCell>

                    {/* steam */}
                    <TableCell>
                        <Link
                            href={`https://steamcommunity.com/profiles/${player.steam_id}`}
                            target="_blank"
                        >
                            {player.steam_id}
                        </Link>
                    </TableCell>

                    {/* name */}
                    <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                            {active && <CircleIcon sx={{ fontSize: 10, color: "green" }} />}
                            {vip && (
                                <Tooltip title="Есть активные привилегии">
                                    <StarsIcon sx={{ fontSize: 16, color: "#b8a26e" }} />
                                </Tooltip>
                            )}
                            {player.name}
                        </Box>
                    </TableCell>

                    <TableCell>{player.game_times}</TableCell>
                    <TableCell>{player.updated_at}</TableCell>

                    {/* actions */}
                    <TableCell align="right">
                        <Box display="flex" justifyContent="flex-end" gap={0.5}>
                            <Tooltip title="Выдать VIP">
                                <IconButton size="small" onClick={() => openModal("vip", player)}>
                                    <EmojiEventsIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>

                            {player.auth_name ? <Tooltip title="Изменить баланс / бонусы">
                                <IconButton size="small" onClick={() => openModal("balance", player)}>
                                    <MonetizationOnIcon fontSize="small" />
                                </IconButton>
                            </Tooltip> : ''}

                            <Tooltip title="Кикнуть">
                                <IconButton size="small" onClick={() => openModal("kick", player)}>
                                    <LogoutIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>

                            <Tooltip title="Забанить">
                                <IconButton size="small" color="error" onClick={() => openModal("ban", player)}>
                                    <BlockIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </TableCell>
                </TableRow>

                {/* collapse */}
                <TableRow>
                    <TableCell colSpan={6} sx={{ p: 0 }}>
                        <Collapse in={open} timeout="auto" unmountOnExit>
                            <Box sx={{ p: 2, bgcolor: "background.default" }}>
                                <Box display="flex" gap={4} flexWrap="wrap">

                                    <Box>
                                        <Typography variant="caption">Первая игра</Typography>
                                        <Typography>{player.created_at}</Typography>
                                    </Box>

                                    <Box>
                                        <Typography variant="caption">Баланс</Typography>
                                        <Box display="flex" alignItems="center" gap={0.5}>
                                            <MonetizationOnIcon fontSize="small" color="action" />
                                            <Typography>{player.balance}</Typography>
                                        </Box>
                                    </Box>

                                    <Box>
                                        <Typography variant="caption">Бонусы</Typography>
                                        <Box display="flex" alignItems="center" gap={0.5}>
                                            <RedeemIcon fontSize="small" color="action" />
                                            <Typography>{player.bonuses}</Typography>
                                        </Box>
                                    </Box>

                                    <Box>
                                        <Typography variant="caption">Аккаунт сайта</Typography>
                                        <Box display="flex" alignItems="center" gap={0.5}>
                                            <SecurityIcon fontSize="small" color="action" />
                                            <Typography>
                                                {player.auth_name
                                                    ? `${player.auth_name} (${player.auth_id})`
                                                    : "не привязан"}
                                            </Typography>
                                        </Box>
                                    </Box>

                                    {vip && (
                                        <Box>
                                            <Typography variant="caption">Привилегии</Typography>
                                            <Box display="flex" gap={1} mt={0.5}>
                                                {player.timed_perms.map((perm, i) => (
                                                    <Chip
                                                        key={i}
                                                        icon={<EmojiEventsIcon sx={{ color: "#b8a26e" }} />}
                                                        label={`${perm.type.toUpperCase()} до ${perm.valid_until}`}
                                                        size="small"
                                                        variant="outlined"
                                                        sx={{
                                                            borderColor: "#b8a26e",
                                                            color: "#b8a26e",
                                                            "& .MuiChip-icon": {
                                                                marginLeft: "4px"
                                                            }
                                                        }}
                                                    />
                                                ))}
                                            </Box>
                                        </Box>
                                    )}

                                </Box>
                            </Box>
                        </Collapse>
                    </TableCell>
                </TableRow>
            </>
        );
    };

    const VipModal = () => {
        const [type, setType] = useState("vip");
        const [days, setDays] = useState(30);

        const handleSubmit = async () => {
            console.log("VIP", {
                steam_id: selectedPlayer.steam_id,
                type,
                days,
                serverId
            });
            let command;
            if (type === 'vip') {
                if (days > 0) command = `addgroup ${selectedPlayer.steam_id} vip ${days}d`;
                else command = `removegroup ${selectedPlayer.steam_id} vip`;
            } else if (type === 'fermer') {
                if (days > 0) command = `addgroup ${selectedPlayer.steam_id} fermer ${days}d`;
                else command = `removegroup ${selectedPlayer.steam_id} fermer`;
            }else if (type === 'tool_unbreakable') {
                command = `grantperm ${selectedPlayer.steam_id} neverwear.tools ${days}d`;
            } else if (type === 'weapon_unbreakable') {
                command = `grantperm ${selectedPlayer.steam_id} neverwear.weapons ${days}d`;
            }

            if (command) {
                let data = await sendRcon(serverId, command, 'command');
                console.log(data);
            }

            setVipModalOpen(false);
        };

        return (
            <Dialog open={vipModalOpen} onClose={() => setVipModalOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Выдать привилегию</DialogTitle>
                <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
                    <FormControl fullWidth size="small">
                        <InputLabel>Тип</InputLabel>
                        <Select value={type} label="Тип" onChange={e => setType(e.target.value)}>
                            <MenuItem value="vip">VIP</MenuItem>
                            <MenuItem value="fermer">Фермер</MenuItem>
                            <MenuItem value="tool_unbreakable">Неломайка инструмента</MenuItem>
                            <MenuItem value="weapon_unbreakable">Неломайка оружия</MenuItem>
                        </Select>
                    </FormControl>

                    <TextField
                        label="На сколько дней"
                        type="number"
                        size="small"
                        value={days}
                        onChange={e => setDays(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setVipModalOpen(false)}>Отмена</Button>
                    <Button variant="contained" onClick={handleSubmit}>
                        Выдать
                    </Button>
                </DialogActions>
            </Dialog>
        );
    };

    const BalanceModal = () => {
        const [balance, setBalance] = useState("");
        const [bonuses, setBonuses] = useState("");

        const handleApply = () => {
            console.log("BALANCE", {
                steam_id: selectedPlayer.steam_id,
                balance,
                bonuses
            });
            setBalanceModalOpen(false);
        };

        return (
            <Dialog open={balanceModalOpen} onClose={() => setBalanceModalOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Изменить баланс</DialogTitle>
                <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
                    <TextField
                        label="Баланс"
                        type="number"
                        size="small"
                        value={balance}
                        onChange={e => setBalance(e.target.value)}
                    />
                    <TextField
                        label="Бонусы"
                        type="number"
                        size="small"
                        value={bonuses}
                        onChange={e => setBonuses(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setBalanceModalOpen(false)}>Отмена</Button>
                    <Button variant="contained" onClick={handleApply}>
                        Применить
                    </Button>
                </DialogActions>
            </Dialog>
        );
    };

    const ConfirmModal = ({ open, onClose, title, onConfirm, danger }) => (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle>{title}</DialogTitle>
            <DialogActions>
                <Button onClick={onClose}>Отмена</Button>
                <Button
                    variant="contained"
                    color={danger ? "error" : "primary"}
                    onClick={onConfirm}
                >
                    Подтвердить
                </Button>
            </DialogActions>
        </Dialog>
    );

    return (
        <Container maxWidth="xl" sx={{ mt: 4 }}>
            <Box sx={{ width: '100%', marginTop: 3 }}>
                <BreadCrumbs hrefs={hrefs} name="Список игроков сервера" />
            </Box>
            <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
            >
                <Typography variant="h4" gutterBottom mt={3}>
                    Список игроков сервера
                </Typography>

                <Box display="flex" gap={2} mt={2} flexDirection={{ xs: 'column', sm: 'row' }}>
                    <TextField
                        label="Поиск по нику или Steam ID"
                        variant="outlined"
                        size="small"
                        fullWidth
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />

                    <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel>Сортировка</InputLabel>
                        <Select
                            value={sortBy}
                            label="Сортировка"
                            onChange={(e) => setSortBy(e.target.value)}
                        >
                            {sortFields.map((f) => (
                                <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                {loading ? (
                    <Box display="flex" justifyContent="center" mt={4}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        <TableContainer component={Paper} sx={{ mt: 2 }}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell />
                                        <TableCell>Steam ID</TableCell>
                                        <TableCell>Игрок</TableCell>
                                        <TableCell>Часы</TableCell>
                                        <TableCell>Был в игре</TableCell>
                                        <TableCell align="right">Действия</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {(showAll ? filtered : filtered.slice(0, LIMIT)).map((player) => (
                                        <PlayerRow key={player.steam_id} player={player} />
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        {filtered.length > LIMIT && (
                            <Box display="flex" justifyContent="center" mt={2}>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() => setShowAll(prev => !prev)}
                                >
                                    {showAll
                                        ? "Свернуть до 50 игроков"
                                        : `Показать всех (${filtered.length})`}
                                </Button>
                            </Box>
                        )}
                    </>
                )}
            </motion.div>

            {selectedPlayer && (
                <>
                    <VipModal />
                    <BalanceModal />
                </>
            )}

            <ConfirmModal
                open={kickModalOpen}
                onClose={() => setKickModalOpen(false)}
                title="Кикнуть игрока?"
                onConfirm={() => {
                    sendRcon(serverId, `kick ${selectedPlayer.steam_id}`, 'command').then(r => {
                        console.log('success')
                    });
                    console.log("KICK", selectedPlayer.steam_id);
                    setKickModalOpen(false);
                }}
            />

            <ConfirmModal
                open={banModalOpen}
                onClose={() => setBanModalOpen(false)}
                title="Забанить игрока?"
                danger
                onConfirm={() => {
                    sendRcon(serverId, `banid ${selectedPlayer.steam_id}`, 'command').then(r => {
                        console.log('success')
                    });
                    console.log("BAN", selectedPlayer.steam_id);
                    setBanModalOpen(false);
                }}
            />
        </Container>
    );
});

export default PlayersPage;
